import React from "react";
import BotInputField from "@common-components/bt-chatbot/bot-components/bot-input-field";
import BotToggleSwitch from "@common-components/bt-chatbot/bot-components/bot-toggle-switch";
import BTbotbutton from "@common-components/bt-chatbot/bot-components/bt-bot-button";
import SelectVariable from "@common-components/bt-chatbot/bot-components/select-variable";
import RowStack from "@common-components/common/row-stack/RowStack";
import { HelpRounded, RemoveCircle } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { Stack } from "@mui/system";
import classes from "./webhook-node.module.scss";
import { useTranslation } from "react-i18next";
import { namespace } from "@common/config/constants";

type ResponseVariable = { variableName: string; responseBody: string };
type SaveResponseAsVariableSlice = {
    isSaveResponseAsVariable: boolean;
    variables: ResponseVariable[];
};
interface LocalWebhookState {
    saveResponseAsVariable: SaveResponseAsVariableSlice;
}
interface LocalCurrentBot {
    botSettings: {
        variables?: Record<string, string[]>;
    };
}

type Props<W extends LocalWebhookState, B extends LocalCurrentBot> = {
    localWebhookState: W;
    setLocalWebhookState: React.Dispatch<React.SetStateAction<W>>;
    localCurrentBot: B;
    setLocalCurrentBot: React.Dispatch<React.SetStateAction<B>>;
    id: string;
};

export default function SaveResponseAsVariable<W extends LocalWebhookState, B extends LocalCurrentBot>({
    localWebhookState,
    setLocalWebhookState,
    localCurrentBot,
    setLocalCurrentBot,
    id,
}: Props<W, B>) {
    const { t } = useTranslation(namespace);
    const title = (title: string, style?: any) => {
        return <div className={classes.title} style={style}>{title}</div>;
    };

    const secondaryTitle = (secondaryTitle: string, style?: any) => {
        return <div className={classes.secondary_title} style={style}>{secondaryTitle}</div>;
    };

    const setVariable = (type: 'variableName' | 'responseBody', value: string, index: number) => {
        setLocalWebhookState((prevState: W) => ({
            ...prevState,
            saveResponseAsVariable: {
                ...prevState.saveResponseAsVariable,
                variables: prevState.saveResponseAsVariable.variables.map((variable: ResponseVariable, i: number) => {
                    if (i === index) {
                        return {
                            ...variable,
                            [type]: value,
                        };
                    } else {
                        return variable;
                    }
                }),
            },
        }));

        if (type === 'variableName') {
            setLocalCurrentBot((prevBot: B) => {
                const updatedVariables = { ...prevBot.botSettings.variables };
                const nodeIdVariables = updatedVariables[id] ? [...(updatedVariables[id] ?? [])] : [];

                // Update the specific variable name in the node's variables array
                nodeIdVariables[index] = value;

                return {
                    ...prevBot,
                    botSettings: {
                        ...prevBot.botSettings,
                        variables: {
                            ...updatedVariables,
                            [id]: nodeIdVariables,
                        },
                    },
                };
            });
        }
    };

    const handleClickRemoveVariable = (index: number) => {
        const variables = [...localWebhookState.saveResponseAsVariable.variables];
        variables.splice(index, 1);

        setLocalWebhookState({
            ...localWebhookState,
            saveResponseAsVariable: {
                ...localWebhookState.saveResponseAsVariable,
                variables: variables,
            },
        });

        setLocalCurrentBot((prevBot: B) => {
            const updatedVariables = { ...prevBot.botSettings.variables };
            const nodeIdVariables = updatedVariables[id] ? [...(updatedVariables[id] ?? [])] : [];
            nodeIdVariables.splice(index, 1);

            return {
                ...prevBot,
                botSettings: {
                    ...prevBot.botSettings,
                    variables: {
                        ...updatedVariables,
                        [id]: nodeIdVariables,
                    },
                },
            };
        });
    };

    const handleAddVariable = () => {
        setLocalWebhookState((prevState: W) => ({
            ...prevState,
            saveResponseAsVariable: {
                ...prevState.saveResponseAsVariable,
                variables: [
                    ...prevState.saveResponseAsVariable.variables,
                    { variableName: '', responseBody: '' },
                ],
            },
        }));

        setLocalCurrentBot((prevBot: B) => {
            const updatedVariables = { ...prevBot.botSettings.variables };
            const nodeIdVariables = updatedVariables[id] ? [...(updatedVariables[id] ?? [])] : [];
            nodeIdVariables.push('');

            return {
                ...prevBot,
                botSettings: {
                    ...prevBot.botSettings,
                    variables: {
                        ...updatedVariables,
                        [id]: nodeIdVariables,
                    },
                },
            };
        });
    };

    const handleToggleSwitch = () => {
        let variables = localWebhookState.saveResponseAsVariable.variables;
        if (variables.length === 0) {
            variables = [{ variableName: '', responseBody: '' }];
            setLocalCurrentBot((prevBot: B) => ({
                ...prevBot,
                botSettings: {
                    ...prevBot.botSettings,
                    variables: {
                        ...prevBot.botSettings.variables,
                        [id]: [''],
                    },
                },
            }));
        }
        setLocalWebhookState((prevState: W) => ({
            ...prevState,
            saveResponseAsVariable: {
                ...prevState.saveResponseAsVariable,
                isSaveResponseAsVariable: !prevState.saveResponseAsVariable.isSaveResponseAsVariable,
                variables,
            },
        }));
    };

    const variableRow = (variable: ResponseVariable, _index: number) => {
        return (
            <Stack key={_index} sx={{ marginBottom: '15px' }}>
                <RowStack sx={{ gap: 2, marginBottom: '5px' }}>
                    <Stack sx={{ height: '110px' }}>
                        <RowStack sx={{ alignItems: 'flex-end' }}>
                            {secondaryTitle(t('chatbotNodes.webhookText.variableName'))}
                            <Tooltip title={t('chatbotNodes.conditionTooltip')}>
                                <HelpRounded sx={{ color: 'var(--color-text)', width: '15px', margin: '0 0 -2px 3px' }} />
                            </Tooltip>
                        </RowStack>
                        <BotInputField
                            value={variable.variableName}
                            onChange={(newValue) => setVariable('variableName', newValue, _index)}
                            placeholder={t('chatbotNodes.webhookText.typeValueOrSelect')}
                        />
                        <div style={{ marginTop: '10px' }}>
                            <SelectVariable buttonSize='small' onSelect={(newValue) => setVariable('variableName', newValue, _index)} />
                        </div>
                    </Stack>
                    <Stack sx={{ height: '110px' }}>
                        {secondaryTitle(t('chatbotNodes.webhookText.entireResponseBody'))}
                        <BotInputField
                            value={variable.responseBody}
                            onChange={(newValue) => setVariable('responseBody', newValue, _index)}
                            placeholder={t('chatbotNodes.webhookText.typeValueOrSelect')}
                        />
                    </Stack>
                    <IconButton
                        disableRipple
                        sx={{
                            zIndex: 2,
                            width: '30px',
                            color: 'var(--bulk-text-secondary)',
                            paddingBottom: '25px',
                            ".MuiSvgIcon-root": {
                                width: '20px',
                                height: '20px',
                            },
                            ".MuiSvgIcon-root:hover": {
                                color: 'red',
                            },
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                        onClick={() => handleClickRemoveVariable(_index)}
                    >
                        <RemoveCircle />
                    </IconButton>
                </RowStack>
            </Stack>
        );
    };

    return (
        <div className={classes.divider}>
            <RowStack sx={{ justifyContent: 'space-between' }}>
                {title(t('chatbotNodes.webhookText.saveResponsesAsVariables'))}
                <BotToggleSwitch
                    onChange={handleToggleSwitch}
                    checked={localWebhookState.saveResponseAsVariable.isSaveResponseAsVariable}
                    isOptional={true}
                    sx={{ marginBottom: '20px' }}
                />
            </RowStack>
            {localWebhookState.saveResponseAsVariable.isSaveResponseAsVariable && (
                <>
                    {secondaryTitle(t('chatbotNodes.webhookText.saveResponseHelpLine1'))}
                    {secondaryTitle(t('chatbotNodes.webhookText.saveResponseHelpLine2'))}
                    {secondaryTitle(t('chatbotNodes.webhookText.saveResponseHelpLine3'))}
                    {secondaryTitle(t('chatbotNodes.webhookText.saveResponseHelpLine4'))}

                    {title(t('chatbotNodes.webhookText.editResponseVariables'), { marginTop: '10px' })}

                    {(localWebhookState?.saveResponseAsVariable?.variables ?? []).map((variable: ResponseVariable, index: number) =>
                        variableRow(variable, index)
                    )}
                    <BTbotbutton styleType="dashed" onClick={handleAddVariable}>
                        {t('chatbotNodes.webhookText.addResponseVariable')}
                    </BTbotbutton>
                </>
            )}
        </div>
    );
}
